<div class="site-analytics-summary-layout-container tw-relative tw-overflow-y-auto tw-h-full">
    <div class="site-analytics-summary-layout-content">
        <!-- Demo Data Alert -->
        @if (domain | isStatsDemoData) {
            <div class="site-analytics-summary__alert">
                <p-message severity="warn">
                    {{ 'siteAnalytics.summary.general.sampleDataDisabled' | transloco }}
                </p-message>
            </div>
        }
        <mon-page-layout
            [header]="'siteAnalytics.summary.general.header' | transloco"
            [subheader]="'siteAnalytics.summary.general.subheader' | transloco"
        >
            <div class="site-analytics-summary__controls">
                <div class="site-analytics-summary__control">
                    <label for="statistics-summary-date-range">
                        {{ 'siteAnalytics.scaffold.dateRange.label' | transloco }}
                    </label>
                    <mon-site-analytics-date-range-picker
                        inputId="statistics-summary-date-range"
                        [ngModel]="primaryDateRange"
                        (ngModelChange)="onPrimaryDateRangeChange($event)"
                        data-test="statistics-summary-date-range"
                    />
                </div>
            </div>

            @if (loading) {
                <div class="site-analytics-summary__loading" data-test="statistics-summary-loading">
                    <p-progress-spinner ariaLabel="loading" />
                </div>
            } @else {
                <div class="site-analytics-summary__cards-grid">
                    <!-- Visitors & Charts Card -->
                    <p-card styleClass="tw-h-full tw-col-span-2">
                        <ng-template #title>
                            <div class="tw-text-lg tw-font-medium">{{ 'siteAnalytics.summary.general.visitorOverview' | transloco }}</div>
                        </ng-template>

                        <div class="site-analytics-summary__metrics-row">
                            <div class="site-analytics-summary__metric">
                                <span class="tw-text-sm">{{ totalVisitsLabel }}</span>
                                <span class="tw-text-2xl">{{ summaryData.visits | monNumber }}</span>
                            </div>
                            <div class="site-analytics-summary__metric">
                                <span class="tw-text-sm">{{ totalVisitorsLabel }}</span>
                                <span class="tw-text-2xl">{{ summaryData.visitors | monNumber }}</span>
                            </div>
                            <div class="site-analytics-summary__metric">
                                <span class="tw-text-sm">{{ 'siteAnalytics.summary.sources.metrics.pageViews' | transloco }}</span>
                                <span class="tw-text-2xl">{{ summaryData.pageviews | monNumber }}</span>
                            </div>
                            <div class="site-analytics-summary__metric">
                                <span class="tw-text-sm">{{ 'siteAnalytics.summary.general.avgVisitDuration' | transloco }}</span>
                                <span class="tw-text-2xl">{{ summaryData.avg_time | monNumber:'00:00:00' }}</span>
                            </div>
                            <div class="site-analytics-summary__metric">
                                <span class="tw-text-sm">{{ 'siteAnalytics.summary.sources.metrics.bounceRate' | transloco }}</span>
                                <span class="tw-text-2xl">{{ summaryData.bounce_rate }}</span>
                            </div>
                        </div>

                        @if (chartData.labels && chartData.labels.length > 0) {
                            <div class="site-analytics-summary__chart-container">
                                <p-chart
                                    [type]="'line'"
                                    [data]="chartData"
                                    [options]="chartOptions"
                                    height="300px"
                                />
                            </div>
                        } @else {
                            <div class="site-analytics-summary__empty-state">
                                <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ 'siteAnalytics.summary.shared.emptyState' | transloco }}</span>
                            </div>
                        }

                        <ng-template #footer>
                            <div class="site-analytics-summary__footer-link">
                                <a uiSref="base.customer.domain.statistics.visitors.summary" class="gel-link tw-inline-flex tw-items-center tw-gap-1">
                                    {{ 'siteAnalytics.summary.general.visitorOverview' | transloco }}
                                </a>
                            </div>
                        </ng-template>
                    </p-card>

                    <!-- Most Visited Pages Card -->
                    <p-card styleClass="tw-h-full">
                        <ng-template #title>
                            <div class="tw-text-lg tw-font-medium">{{ 'siteAnalytics.summary.general.mostVisitedPages' | transloco }}</div>
                        </ng-template>

                        @if (mostVisitedPages.length > 0) {
                            <div class="site-analytics-summary__table-container">
                                <p-table
                                    [value]="mostVisitedPages"
                                    [styleClass]="'fix-table-header-terminals'"
                                    tableStyleClass="tw-table-fixed"
                                    [scrollable]="true"
                                    scrollHeight="flex"
                                    responsiveLayout="scroll">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th scope="col" class="tw-text-left" [style.width.%]="64">{{ 'siteAnalytics.summary.general.pageUrl' | transloco }}</th>
                                        <th class="tw-text-right" scope="col" [style.width.%]="18">{{ 'siteAnalytics.scaffold.table.column.visits' | transloco }}</th>
                                        <th class="tw-text-right" scope="col" [style.width.%]="18">{{ 'siteAnalytics.summary.sources.metrics.pageViews' | transloco }}</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-page>
                                    <tr>
                                        <td>
                                            @if (page.url !== '0') {
                                                <a
                                                    [href]="page.url"
                                                    target="_blank"
                                                    class="tw-flex tw-items-center tw-gap-2 tw-min-w-0"
                                                    [title]="page.url">
                                                    <i class="pi pi-external-link"></i>
                                                    <span class="tw-truncate tw-min-w-0">{{ page.url }}</span>
                                                </a>
                                            } @else {
                                                {{ page.label }}
                                            }
                                        </td>
                                        <td class="tw-text-right">{{ page.visits | monNumber }}</td>
                                        <td class="tw-text-right">{{ page.pageviews | monNumber }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                            </div>
                        } @else {
                            <div class="site-analytics-summary__empty-state">
                                <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ 'siteAnalytics.summary.shared.emptyState' | transloco }}</span>
                            </div>
                        }

                        <ng-template #footer>
                            <div class="site-analytics-summary__footer-link">
                                <a uiSref="base.customer.domain.statistics.pages.mostPopular" class="gel-link tw-inline-flex tw-items-center tw-gap-1">
                                    {{ 'siteAnalytics.summary.general.mostVisitedPages' | transloco }}
                                </a>
                            </div>
                        </ng-template>
                    </p-card>

                    <!-- Traffic Sources Card -->
                    <p-card styleClass="tw-h-full">
                        <ng-template #title>
                            <div class="tw-text-lg tw-font-medium">{{ 'siteAnalytics.summary.general.trafficSources' | transloco }}</div>
                        </ng-template>

                        @if (hasTrafficSourceData) {
                            <div class="site-analytics-summary__chart-container site-analytics-summary__chart-container--small">
                                <p-chart
                                    [type]="'bar'"
                                    [data]="trafficSourceChartData"
                                    [options]="trafficSourceChartOptions"
                                    height="150px"
                                />
                            </div>

                            <div class="site-analytics-summary__traffic-sources-list">
                                <div class="site-analytics-summary__traffic-source">
                                    <div class="tw-flex tw-items-center tw-gap-3">
                                        <div class="tw-w-1 tw-h-10 tw-rounded-full" [style.background-color]="trafficSourceColors.organic"></div>
                                        <div class="tw-flex-1">
                                            <div class="tw-flex tw-justify-between tw-items-center tw-mb-1">
                                                <span>{{ 'siteAnalytics.summary.sources.referring.searchEngines' | transloco }}</span>
                                                <span class="tw-text-sm" style="color: var(--p-text-muted-color);">({{ getTrafficSourcePercentage(trafficSourceCounts.organic) | monNumber:'0.0' }}%)</span>
                                            </div>
                                            <div class="tw-flex tw-justify-between tw-items-center">
                                                <p-progressbar
                                                    [value]="getTrafficSourcePercentage(trafficSourceCounts.organic)"
                                                    [showValue]="false"
                                                />
                                                <span class="tw-font-medium tw-ml-3">{{ trafficSourceCounts.organic | monNumber }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="site-analytics-summary__traffic-source">
                                    <div class="tw-flex tw-items-center tw-gap-3">
                                        <div class="tw-w-1 tw-h-10 tw-rounded-full" [style.background-color]="trafficSourceColors.direct"></div>
                                        <div class="tw-flex-1">
                                            <div class="tw-flex tw-justify-between tw-items-center tw-mb-1">
                                                <span>{{ 'siteAnalytics.summary.sources.series.directVisits' | transloco }}</span>
                                                <span class="tw-text-sm" style="color: var(--p-text-muted-color);">({{ getTrafficSourcePercentage(trafficSourceCounts.direct) | monNumber:'0.0' }}%)</span>
                                            </div>
                                            <div class="tw-flex tw-justify-between tw-items-center">
                                                <p-progressbar
                                                    [value]="getTrafficSourcePercentage(trafficSourceCounts.direct)"
                                                    [showValue]="false"
                                                />
                                                <span class="tw-font-medium tw-ml-3">{{ trafficSourceCounts.direct | monNumber }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="site-analytics-summary__traffic-source">
                                    <div class="tw-flex tw-items-center tw-gap-3">
                                        <div class="tw-w-1 tw-h-10 tw-rounded-full" [style.background-color]="trafficSourceColors.referring"></div>
                                        <div class="tw-flex-1">
                                            <div class="tw-flex tw-justify-between tw-items-center tw-mb-1">
                                                <span>{{ 'siteAnalytics.summary.sources.series.referringVisits' | transloco }}</span>
                                                <span class="tw-text-sm" style="color: var(--p-text-muted-color);">({{ getTrafficSourcePercentage(trafficSourceCounts.referring) | monNumber:'0.0' }}%)</span>
                                            </div>
                                            <div class="tw-flex tw-justify-between tw-items-center">
                                                <p-progressbar
                                                    [value]="getTrafficSourcePercentage(trafficSourceCounts.referring)"
                                                    [showValue]="false"
                                                />
                                                <span class="tw-font-medium tw-ml-3">{{ trafficSourceCounts.referring | monNumber }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="site-analytics-summary__traffic-source">
                                    <div class="tw-flex tw-items-center tw-gap-3">
                                        <div class="tw-w-1 tw-h-10 tw-rounded-full" [style.background-color]="trafficSourceColors.social"></div>
                                        <div class="tw-flex-1">
                                            <div class="tw-flex tw-justify-between tw-items-center tw-mb-1">
                                                <span>{{ 'siteAnalytics.summary.sources.series.socialVisits' | transloco }}</span>
                                                <span class="tw-text-sm" style="color: var(--p-text-muted-color);">({{ getTrafficSourcePercentage(trafficSourceCounts.social) | monNumber:'0.0' }}%)</span>
                                            </div>
                                            <div class="tw-flex tw-justify-between tw-items-center">
                                                <p-progressbar
                                                    [value]="getTrafficSourcePercentage(trafficSourceCounts.social)"
                                                    [showValue]="false"
                                                />
                                                <span class="tw-font-medium tw-ml-3">{{ trafficSourceCounts.social | monNumber }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="site-analytics-summary__traffic-source">
                                    <div class="tw-flex tw-items-center tw-gap-3">
                                        <div class="tw-w-1 tw-h-10 tw-rounded-full" [style.background-color]="trafficSourceColors.paid"></div>
                                        <div class="tw-flex-1">
                                            <div class="tw-flex tw-justify-between tw-items-center tw-mb-1">
                                                <span>{{ 'siteAnalytics.summary.sources.series.campaignVisits' | transloco }}</span>
                                                <span class="tw-text-sm" style="color: var(--p-text-muted-color);">({{ getTrafficSourcePercentage(trafficSourceCounts.paid) | monNumber:'0.0' }}%)</span>
                                            </div>
                                            <div class="tw-flex tw-justify-between tw-items-center">
                                                <p-progressbar
                                                    [value]="getTrafficSourcePercentage(trafficSourceCounts.paid)"
                                                    [showValue]="false"
                                                />
                                                <span class="tw-font-medium tw-ml-3">{{ trafficSourceCounts.paid | monNumber }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        } @else {
                            <div class="site-analytics-summary__empty-state">
                                <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ 'siteAnalytics.summary.shared.emptyState' | transloco }}</span>
                            </div>
                        }

                        <ng-template #footer>
                            <div class="site-analytics-summary__footer-link">
                                <a uiSref="base.customer.domain.statistics.sources.summary" class="gel-link tw-inline-flex tw-items-center tw-gap-1">
                                    {{ 'siteAnalytics.summary.general.trafficSources' | transloco }}
                                </a>
                            </div>
                        </ng-template>
                    </p-card>

                    <!-- Most Frequent Card -->
                    <p-card styleClass="tw-h-full">
                        <ng-template #title>
                            <div class="tw-text-lg tw-font-medium">{{ 'siteAnalytics.summary.general.mostFrequent' | transloco }}</div>
                        </ng-template>

                        @if (!allMostFrequentEmpty) {
                            <div class="site-analytics-summary__most-frequent-grid">
                                @for (type of mostFrequentTypes; track type.key) {
                                    @let data = mostFrequentData[type.key];
                                    @if (data && data.title) {
                                        @let iconClass = getMostFrequentIcon(data);
                                        <div class="site-analytics-summary__most-frequent-item">
                                            <i [class]="iconClass + ' site-analytics-summary__most-frequent-icon'"></i>
                                            <div class="site-analytics-summary__most-frequent-label">{{ data.title }}</div>
                                            <div class="site-analytics-summary__most-frequent-value text-semibold">{{ data.label }}</div>
                                            <div class="site-analytics-summary__most-frequent-percentage">
                                                {{ (data.value / data.total) | monNumber:'0.0%' }}
                                            </div>
                                            <p-progressbar
                                                [value]="data.value * 100 / data.total"
                                                [showValue]="false"
                                            />
                                        </div>
                                    }
                                }
                            </div>
                        } @else {
                            <div class="site-analytics-summary__empty-state">
                                <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ 'siteAnalytics.summary.shared.emptyState' | transloco }}</span>
                            </div>
                        }

                        <ng-template #footer>
                            <div class="site-analytics-summary__footer-link">
                                <a uiSref="base.customer.domain.statistics.visitors.summary" class="gel-link tw-inline-flex tw-items-center tw-gap-1">
                                    {{ 'siteAnalytics.summary.general.visitorOverview' | transloco }}
                                </a>
                            </div>
                        </ng-template>
                    </p-card>
                </div>
            }
        </mon-page-layout>
    </div>
</div>
